import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getGoogleInvitationRedirect, validateInvitationToken } from "../../../../redux/features/invitation/invitationSlice";
import LappaLogo from "img/logos/lappa-logo.svg";
import { privacyPolicyPath, termsOfUsePath } from "constants";
import "./InviteAccept.sass";
import useAppDispatch from "hooks/useAppDispatch";

export default function InviteAccept() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = searchParams.get("token");

  const validateData = useSelector((state) => state.invitation?.validateData || null);
  const validateStatus = useSelector((state) => state.invitation?.validateStatus || "idle");
  const googleRedirectStatus = useSelector(
    (state) => state.invitation?.googleRedirectStatus || "idle"
  );

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
      return;
    }

    if (validateData?.token === token) return;

    dispatch(validateInvitationToken(token));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, validateData]);

  useEffect(() => {
    if (!token) {
      setError(true);
      return;
    }

    if (validateStatus === "failed") {
      setError(true);
      setInvite(null);
      return;
    }

    if (validateStatus === "succeeded" && validateData && validateData.token === token) {
      setInvite({
        inviterName: validateData.inviter_name,
        email: validateData.email,
        companyName: validateData.company_name,
        message: validateData.message,
        joiningAs: validateData.joining_as,
      });
      setError(false);
      return;
    }

    setError(false);
  }, [token, validateStatus, validateData]);

  const handleContinueSignup = () => {
    navigate(`/invite/profile?token=${encodeURIComponent(token || "")}`);
  };

  const handleContinueWithGoogle = async () => {
    if (!token) {
      setError(true);
      return;
    }

    try {
      const { redict_url } = await dispatch(getGoogleInvitationRedirect(token));
      const apiBase = process.env.REACT_APP_API_URL || window.location.origin;
      const apiOrigin = new URL(apiBase).origin;
      const target = new URL(redict_url, apiOrigin).toString();
      window.location.assign(target);
    } catch (err) {
      console.error("Error during Google OAuth request:", err);
      setError(true);
    }
  };

  const handleLogIn = () => navigate("/login");

  const isValidating = validateStatus === "loading" || validateStatus === "idle";
  const shouldShowInvalid = !token || validateStatus === "failed" || (validateStatus === "succeeded" && (!validateData || validateData.token !== token));
  const shouldShowContent = validateStatus === "succeeded" && validateData && validateData.token === token;

  const inviterName = validateData?.inviter_name || invite?.inviterName || "Alex Smith";
  const email = validateData?.email || invite?.email || "";
  const title = validateData?.message || (inviterName ? `You've accepted ${inviterName}'s invitation` : "You've accepted the invitation");
  const joiningText = validateData?.joining_as || (email ? `You're joining as ${email}` : "");


  return (
    <div className="InviteAcceptPage">
      <header className="InviteAcceptPage__header">
        <img className="InviteAcceptPage__logo" src={LappaLogo} alt="LAPPA" />
      </header>

      <main className="InviteAcceptPage__main">
        {shouldShowInvalid ? (
          <>
            <h1 className="InviteAcceptPage__title">{t("auth.invite.invalidInvite")}</h1>
            <p className="InviteAcceptPage__subTitle">
              {t("auth.invite.invalidOrExpired")}
            </p>
          </>
        ) : isValidating ? (
          <>
            <h1 className="InviteAcceptPage__title">{t("auth.invite.checkingInvite")}</h1>
          </>
        ) : shouldShowContent ? (
          <>
            <h1 className="InviteAcceptPage__title">
              {title}
            </h1>

            <p className="InviteAcceptPage__tos">
              By continuing, you agree to the{" "}
              <a
                className="InviteAcceptPage__link"
                href={termsOfUsePath}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="InviteAcceptPage__link"
                href={privacyPolicyPath}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </p>

            <div className="InviteAcceptPage__spacer32" />

            <button
              className="InviteAcceptPage__btn InviteAcceptPage__btn--primary"
              type="button"
              onClick={handleContinueSignup}
            >
              {t("auth.invite.continueSigningUp")}
            </button>

            <div className="InviteAcceptPage__spacer24" />

            <div className="InviteAcceptPage__or">
              <span className="InviteAcceptPage__orLine" />
              <span className="InviteAcceptPage__orText">{t("auth.invite.or")}</span>
              <span className="InviteAcceptPage__orLine" />
            </div>

            <div className="InviteAcceptPage__spacer24" />

            <button
              className="InviteAcceptPage__btn InviteAcceptPage__btn--google"
              type="button"
              onClick={handleContinueWithGoogle}
              disabled={googleRedirectStatus === "loading"}
            >
              <span className="InviteAcceptPage__googleIcon" aria-hidden="true" />
              {googleRedirectStatus === "loading" ? t("auth.invite.loading") : t("auth.invite.continueWithGoogle")}
            </button>

            <div className="InviteAcceptPage__spacer32" />

            {joiningText && (
              <p className="InviteAcceptPage__joining">
                {joiningText}
              </p>
            )}

            <div className="InviteAcceptPage__spacer6" />

            <button
              className="InviteAcceptPage__wrong"
              type="button"
              onClick={handleLogIn}
            >
              {t("auth.invite.wrongAccountLogin")} <span className="InviteAcceptPage__link">{t("auth.invite.logInInstead")}</span> {t("auth.invite.instead")}
            </button>
          </>
        ) : (
          <>
            <h1 className="InviteAcceptPage__title">{t("auth.invite.checkingInvite")}</h1>
          </>
        )}
      </main>
    </div>
  );
}
